"use client";
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import Link from 'next/link';

export default function NotFound() {
  const [mounted, setMounted] = useState(false);
  const [isNight, setIsNight] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Snowflakes with different colors for day/night
  const snowflakes = Array.from({ length: 50 }).map((_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 8,
    duration: 8 + Math.random() * 10,
    size: 0.5 + Math.random() * 1,
    opacity: 0.3 + Math.random() * 0.5,
    sway: (Math.random() - 0.5) * 40,
  }));

  // Stars for night mode
  const stars = Array.from({ length: 80 }).map((_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 60, // Only in upper 60% of screen
    delay: Math.random() * 3,
    duration: 2 + Math.random() * 3,
    size: 0.5 + Math.random() * 0.8,
    opacity: 0.4 + Math.random() * 0.6,
  }));

  // Fire particles
  const fireParticles = Array.from({ length: 15 }).map((_, i) => ({
    id: i,
    delay: Math.random() * 2,
    duration: 1 + Math.random() * 2,
    size: 0.3 + Math.random() * 0.5,
    sway: (Math.random() - 0.5) * 20,
  }));

  const SnowmanSVG = () => (
    <motion.div
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ 
        duration: 0.8, 
        type: 'spring', 
        bounce: 0.3,
        delay: 0.5 
      }}
      className="flex justify-center mb-4 md:mb-6 relative"
    >
      {/* Campfire - only visible at night */}
      {isNight && (
        <motion.div
          className="absolute -bottom-8 left-1/2 transform -translate-x-1/2"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 1 }}
        >
          <svg width="80" height="40" viewBox="0 0 80 40">
            {/* Logs */}
            <rect x="10" y="30" width="60" height="6" rx="3" fill="#8B4513" transform="rotate(-5 40 33)" />
            <rect x="15" y="28" width="50" height="5" rx="2.5" fill="#A0522D" transform="rotate(8 40 30)" />
            <rect x="20" y="32" width="40" height="4" rx="2" fill="#654321" />
            
            {/* Fire flames */}
            <motion.path
              d="M25 30 Q30 20 35 25 Q40 15 45 22 Q50 18 55 25 Q52 30 40 28 Q35 32 25 30"
              fill="url(#fireGradient)"
              animate={{
                d: [
                  "M25 30 Q30 20 35 25 Q40 15 45 22 Q50 18 55 25 Q52 30 40 28 Q35 32 25 30",
                  "M26 30 Q32 18 36 24 Q42 12 46 20 Q52 16 56 24 Q54 30 42 27 Q36 33 26 30",
                  "M25 30 Q30 20 35 25 Q40 15 45 22 Q50 18 55 25 Q52 30 40 28 Q35 32 25 30"
                ]
              }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            />
            
            <defs>
              <radialGradient id="fireGradient" cx="50%" cy="80%">
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="30%" stopColor="#FF6B35" />
                <stop offset="70%" stopColor="#FF4500" />
                <stop offset="100%" stopColor="#DC143C" />
              </radialGradient>
            </defs>
          </svg>
          
          {/* Fire particles/sparks */}
          {fireParticles.map((particle) => (
            <motion.div
              key={particle.id}
              className="absolute w-1 h-1 bg-orange-400 rounded-full"
              style={{
                left: `${40 + particle.sway}px`,
                bottom: '35px',
              }}
              initial={{ opacity: 0, y: 0, scale: 0 }}
              animate={{
                opacity: [0, 1, 0],
                y: [-20, -40],
                scale: [0, particle.size, 0],
                x: [0, particle.sway, particle.sway * 2],
              }}
              transition={{
                duration: particle.duration,
                delay: particle.delay,
                repeat: Infinity,
                ease: "easeOut",
              }}
            />
          ))}
        </motion.div>
      )}

      <svg
        width="160"
        height="180"
        viewBox="0 0 160 180"
        className="drop-shadow-sm w-[120px] md:w-[160px] z-10"
      >
        <defs>
          <radialGradient id={isNight ? "snowGradientNight" : "snowGradient"} cx="30%" cy="30%">
            {isNight ? (
              <>
                <stop offset="0%" stopColor="#e2e8f0" />
                <stop offset="70%" stopColor="#cbd5e1" />
                <stop offset="100%" stopColor="#94a3b8" />
              </>
            ) : (
              <>
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="70%" stopColor="#f8fafc" />
                <stop offset="100%" stopColor="#e2e8f0" />
              </>
            )}
          </radialGradient>
        </defs>
        
        {/* Shadow */}
        <ellipse cx="80" cy="170" rx="35" ry="6" fill={isNight ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.1)"} />
        
        {/* Bottom snowball */}
        <motion.circle
          cx="80"
          cy="145"
          r="28"
          fill={`url(#${isNight ? "snowGradientNight" : "snowGradient"})`}
          stroke={isNight ? "#64748b" : "#e2e8f0"}
          strokeWidth="1"
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        
        {/* Middle snowball */}
        <motion.circle
          cx="80"
          cy="105"
          r="22"
          fill={`url(#${isNight ? "snowGradientNight" : "snowGradient"})`}
          stroke={isNight ? "#64748b" : "#e2e8f0"}
          strokeWidth="1"
          animate={{ scale: [1, 1.03, 1] }}
          transition={{ duration: 3.5, repeat: Infinity, delay: 0.5, ease: "easeInOut" }}
        />
        
        {/* Head */}
        <motion.circle
          cx="80"
          cy="70"
          r="18"
          fill={`url(#${isNight ? "snowGradientNight" : "snowGradient"})`}
          stroke={isNight ? "#64748b" : "#e2e8f0"}
          strokeWidth="1"
          animate={{ 
            scale: [1, 1.04, 1],
            rotate: [-0.5, 0.5, -0.5]
          }}
          transition={{ duration: 3, repeat: Infinity, delay: 1, ease: "easeInOut" }}
        />
        
        {/* Top Hat */}
        {/* Hat crown */}
        <rect x="68" y="40" width="24" height="18" fill="#1f2937" rx="2" />
        {/* Hat brim */}
        <ellipse cx="80" cy="58" rx="18" ry="4" fill="#1f2937" />
        {/* Hat band */}
        <rect x="68" y="52" width="24" height="3" fill="#dc2626" rx="0.5" />
        {/* Hat highlight */}
        <rect x="70" y="42" width="2.5" height="12" fill="#4b5563" rx="1" opacity="0.4" />
        
        {/* Eyes */}
        <circle cx="74" cy="65" r="2" fill="#374151" />
        <circle cx="86" cy="65" r="2" fill="#374151" />
        
        {/* Carrot nose */}
        <polygon
          points="80,70 90,72 80,74"
          fill="#f97316"
          stroke="#ea580c"
          strokeWidth="1"
        />
        
        {/* Smile */}
        <path
          d="M 74 78 Q 80 83 86 78"
          stroke="#374151"
          strokeWidth="1.5"
          fill="none"
          strokeLinecap="round"
        />
        
        {/* Red Muffler */}
        <rect x="70" y="85" width="20" height="12" fill="#ef4444" rx="2" />
        <path
          d="M 70 97 L 60 95 L 65 90 L 70 85"
          fill="#dc2626"
        />
        <path
          d="M 90 97 L 100 95 L 95 90 L 90 85"
          fill="#dc2626"
        />
        
        {/* Buttons */}
        <circle cx="80" cy="95" r="1.5" fill="#374151" />
        <circle cx="80" cy="105" r="1.5" fill="#374151" />
        <circle cx="80" cy="115" r="1.5" fill="#374151" />
        
        {/* Arms */}
        <line x1="58" y1="100" x2="45" y2="95" stroke="#8b4513" strokeWidth="2" strokeLinecap="round" />
        <line x1="102" y1="100" x2="115" y2="95" stroke="#8b4513" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </motion.div>
  );

  if (!mounted) return null;

  return (
    <motion.div 
      className={`min-h-screen overflow-hidden relative transition-all duration-1000 ${
        isNight 
          ? 'bg-gradient-to-b from-indigo-900 via-purple-900 to-slate-900' 
          : 'bg-gradient-to-b from-gray-50 to-blue-50'
      }`}
      animate={{
        background: isNight 
          ? 'linear-gradient(to bottom, #312e81, #581c87, #0f172a)'
          : 'linear-gradient(to bottom, #f9fafb, #dbeafe)'
      }}
      transition={{ duration: 1 }}
    >
      {/* Enhanced snowflakes */}
      {snowflakes.map((flake) => (
        <motion.div
          key={flake.id}
          className={`absolute pointer-events-none select-none ${
            isNight ? 'text-blue-100' : 'text-blue-200'
          }`}
          style={{
            left: `${flake.left}%`,
            fontSize: `${flake.size}rem`,
            opacity: flake.opacity,
          }}
          initial={{ y: -30, opacity: 0 }}
          animate={{
            y: '110vh',
            opacity: [0, flake.opacity, flake.opacity, 0],
            x: [0, flake.sway, -flake.sway, 0],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: flake.duration,
            delay: flake.delay,
            repeat: Infinity,
            ease: 'linear',
          }}
        >
          ❄
        </motion.div>
      ))}

      {/* Stars - only visible at night */}
      {isNight && stars.map((star) => (
        <motion.div
          key={star.id}
          className="absolute text-yellow-200 pointer-events-none select-none"
          style={{
            left: `${star.left}%`,
            top: `${star.top}%`,
            fontSize: `${star.size}rem`,
          }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{
            opacity: [0, star.opacity, star.opacity * 0.3, star.opacity],
            scale: [0, 1, 0.8, 1],
          }}
          transition={{
            duration: star.duration,
            delay: star.delay,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          ✦
        </motion.div>
      ))}

      {/* Main content */}
      <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 md:px-8 md:py-16 relative z-10">
        
        {/* 404 heading */}
        <motion.h1
          className={`text-7xl sm:text-8xl md:text-9xl font-bold mb-4 md:mb-6 transition-colors duration-1000 ${
            isNight ? 'text-blue-100' : 'text-gray-700'
          }`}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          404
        </motion.h1>

        {/* Snowman */}
        <SnowmanSVG />

        {/* Subtitle */}
        <motion.h2 
          className={`text-2xl sm:text-3xl md:text-4xl font-semibold mb-3 md:mb-4 text-center flex items-center gap-2 transition-colors duration-1000 ${
            isNight ? 'text-blue-100' : 'text-gray-600'
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <span>❄️</span>
          Page Not Found
          <span>❄️</span>
        </motion.h2>

        {/* Description */}
        <motion.p 
          className={`text-base md:text-lg mb-6 md:mb-8 text-center max-w-md md:max-w-2xl px-4 transition-colors duration-1000 ${
            isNight ? 'text-blue-200' : 'text-gray-600'
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          {isNight 
            ? "The page you're looking for is lost in the starry winter night. Let our cozy snowman by the fire guide you back home!" 
            : "The page you're looking for is buried under the snow. Let our snowman guide you back home!"
          }
        </motion.p>

        {/* CTA Button and Toggle Button Container */}
        <motion.div
          className="flex flex-col sm:flex-row items-center gap-4 mt-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          {/* CTA Button */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Link 
              href="/"
              className={`font-medium py-2 px-6 md:py-3 md:px-8 rounded-lg transition-all duration-300 shadow-lg flex items-center gap-2 cursor-pointer ${
                isNight 
                  ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-orange-500/25' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              <span>🏠</span>
              Go Back Home
            </Link>
          </motion.div>

          {/* Toggle button - now positioned near the CTA button */}
          <motion.button
            className={`p-3 rounded-full shadow-lg transition-all duration-300 ${
              isNight 
                ? 'bg-yellow-400 hover:bg-yellow-300 text-gray-900' 
                : 'bg-indigo-600 hover:bg-indigo-700 text-white'
            }`}
            onClick={() => setIsNight(!isNight)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1, duration: 0.5 }}
          >
            <motion.div
              animate={{ rotate: isNight ? 0 : 180 }}
              transition={{ duration: 0.5 }}
            >
              {isNight ? '☀️' : '🌙'}
            </motion.div>
          </motion.button>
        </motion.div>

        {/* Atmospheric text for night mode */}
        {isNight && (
          <motion.p
            className="text-blue-300 text-sm mt-8 text-center opacity-75"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.75 }}
            transition={{ delay: 1.5, duration: 1 }}
          >
            ✨ A peaceful winter night under the stars ✨
          </motion.p>
        )}
      </div>

      {/* Subtle glow effect for night mode */}
      {isNight && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center bottom, rgba(251, 146, 60, 0.1) 0%, transparent 70%)'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2, delay: 1 }}
        />
      )}
    </motion.div>
  );
}